import React, { useState } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import PropdocsModal from '../../../components/modal/PropdocsModal';

type DeleteFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  teamId: string;
  onSuccess: () => void;
  hasChildren?: boolean;
};

const DeleteFolderModal = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  teamId,
  onSuccess,
  hasChildren = false,
}: DeleteFolderModalProps) => {
  const { formatMessage } = useIntl();
  const [confirmationStep, setConfirmationStep] = useState<'initial' | 'final'>('initial');
  const [isDeleting, setIsDeleting] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  const [deleteTemplateFolder] = useMutation(mutations.deleteTemplateFolder as any, {
    onCompleted: () => {
      notifySuccess({ message: 'Folder deleted successfully' });
      onSuccess();
      handleClose();
      setIsDeleting(false);
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to delete folder' });
      setIsDeleting(false);
    },
  });

  const handleClose = () => {
    setConfirmationStep('initial');
    setIsDeleting(false);
    onClose();
  };

  const handleContinue = () => {
    setConfirmationStep('final');
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTemplateFolder({
        variables: {
          id: folderId,
          force: true, // Force delete with contents
        },
        refetchQueries: [
          {
            query: queriesV2.getTemplateFolders,
            variables: { teamId, folderType: 'template' },
          },
          {
            query: queriesV2.getDocumentTemplates,
            variables: { teamId, templateType: 'agreement' },
          },
        ],
      });
    } catch (error) {
      console.error('Error deleting folder:', error);
      notifyError({ message: 'Failed to delete folder' });
      setIsDeleting(false);
    }
  };

  const renderInitialConfirmation = () => (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="mb-3">
          <strong>
            <FormattedMessage
              id="message.deleting-folder"
              defaultMessage="Deleting: {folderName}"
              values={{ folderName }}
            />
          </strong>
        </div>

        <Alert variant="warning">
          <p className="mb-2">
            <strong>
              <FormattedMessage id="warning.title" defaultMessage="Warning" />
            </strong>
          </p>
          <p>
            {hasChildren ? (
              <FormattedMessage
                id="folders.delete.warning.with-contents"
                defaultMessage="This folder contains subfolders and/or templates. Deleting this folder will archive all templates (move them to the root level and hide them) and permanently delete all subfolders."
              />
            ) : (
              <FormattedMessage
                id="folders.delete.warning.empty"
                defaultMessage="This action will permanently delete the folder."
              />
            )}
          </p>
          <p className="mb-0">
            <strong>
              <FormattedMessage
                id="folders.delete.warning.irreversible"
                defaultMessage="This action cannot be undone."
              />
            </strong>
          </p>
        </Alert>

        <p>
          <FormattedMessage id="folders.delete.continue-question" defaultMessage="Are you sure you want to continue?" />
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
        <Button variant="link" className="text-secondary" onClick={handleClose}>
          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="warning" onClick={handleContinue}>
          <TitleFormattedMessage id="cta.continue" defaultMessage="Continue" />
        </Button>
      </div>
    </>
  );

  const renderFinalConfirmation = () => (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="mb-3">
          <strong>
            <FormattedMessage
              id="message.confirm-deleting-folder"
              defaultMessage="Confirm deletion of: {folderName}"
              values={{ folderName }}
            />
          </strong>
        </div>

        <Alert variant="danger">
          <p className="mb-2">
            <strong>
              <FormattedMessage id="danger.final-warning" defaultMessage="Final Warning" />
            </strong>
          </p>
          <p>
            <FormattedMessage
              id="folders.delete.final-warning"
              defaultMessage="You are about to delete this folder. Any templates inside will be archived (moved to root level and hidden - you can reactivate them later). Subfolders will be permanently deleted."
            />
          </p>
          <p className="mb-0">
            <strong>
              <FormattedMessage
                id="folders.delete.are-you-sure"
                defaultMessage="Are you sure you want to delete this folder and archive its templates?"
              />
            </strong>
          </p>
        </Alert>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
        <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isDeleting}>
          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
          {isDeleting ? (
            <FormattedMessage id="cta.deleting" defaultMessage="Deleting..." />
          ) : (
            <TitleFormattedMessage id="cta.delete" defaultMessage="Delete" />
          )}
        </Button>
      </div>
    </>
  );

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="lg"
      disableClose={isDeleting}
      title={
        confirmationStep === 'initial' ? (
          <FormattedMessage id="title.delete-folder" defaultMessage="Delete Folder" />
        ) : (
          <FormattedMessage id="title.confirm-delete-folder" defaultMessage="Confirm Folder Deletion" />
        )
      }
    >
      {confirmationStep === 'initial' ? renderInitialConfirmation() : renderFinalConfirmation()}
    </PropdocsModal>
  );
};

export default DeleteFolderModal;
